import {
  applySnapshot,
  flow,
  Instance,
  SnapshotIn,
  types,
} from "mobx-state-tree";
import { StopEmailCycleRPC } from "@pulse/pulse-rpcs/lib/email-cycle/stopEmailCycleRPC";
import { EMPTY_CHARACTER } from "@pulse/shared-components";
import { getAPIClient } from "../../networking/APIClient";
import { LeoUUID } from "@surya-digital/leo-ts-runtime";
import { LeoRPCResult } from "@surya-digital/leo-ts-types";
import { useStopEmailCycleRPCClientImpl } from "../rpcs/RPC";

const getInitialState = (): SnapshotIn<typeof StopEmailCycleStore> => ({
  scheduleId: EMPTY_CHARACTER,
  loadingStopEmailCycle: false,
  stopEmailCycleRPCError: null,
});

export const StopEmailCycleStore = types
  .model("StopEmailCycleStore", {
    scheduleId: types.string,
    loadingStopEmailCycle: types.boolean,
    stopEmailCycleRPCError: types.maybeNull(
      types.enumeration(
        "StopEmailCycleRPCError",
        Object.values(StopEmailCycleRPC.RPCError),
      ),
    ),
  })
  .actions((store) => ({
    setScheduleId: (scheduleId: string): void => {
      store.scheduleId = scheduleId;
    },
    stopEmailCycle: flow(function* () {
      try {
        store.loadingStopEmailCycle = true;
        store.stopEmailCycleRPCError = null;

        const apiClient = getAPIClient(store);
        const request = new StopEmailCycleRPC.Request(
          new LeoUUID(store.scheduleId),
        );

        const result: LeoRPCResult<
          StopEmailCycleRPC.Response,
          StopEmailCycleRPC.Errors.Errors
        > = yield useStopEmailCycleRPCClientImpl(apiClient).execute(request);

        if ("response" in result) return;

        switch (result.error.code) {
          case StopEmailCycleRPC.RPCError.InvalidScheduleId:
          case StopEmailCycleRPC.RPCError.ScheduleNotInProgress:
            store.stopEmailCycleRPCError = result.error.code;
            break;
          default:
            result.error.code satisfies never;
        }
      } finally {
        store.loadingStopEmailCycle = false;
      }
    }),
    resetStore: (): void => applySnapshot(store, getInitialState()),
  }));

export const createStopEmailCycleStore = (): Instance<
  typeof StopEmailCycleStore
> => StopEmailCycleStore.create(getInitialState());
